import React, { useEffect, useState } from "react"
import { Text, Box, Flex, Heading } from "@chakra-ui/react"
import {
  IngredientsIcon as DropsIcon,
  AMIcon,
  PMIcon,
  BurnTimeIcon,
} from "components/SvgIcons"
import type { VariantType } from "@shared/graphql.types"
import { VideoPlayer } from "components/VideoPlayer"
import { useSegment } from "tracking"
import { GenericMarkdown } from "../GenericMarkdown"

type Props = {
  variant: VariantType
}

const mapIcons = {
  drops: <DropsIcon boxSize="2.2rem" />,
  time: <AMIcon boxSize="2.2rem" />,
  hourglass: <AMIcon boxSize="2.2rem" />,
  AM: <AMIcon boxSize="2.2rem" />,
  PM: <PMIcon boxSize="2.2rem" />,
  BurnTime: <BurnTimeIcon boxSize="2.2rem" />,
}
const scrollThreshold = 120

export const HowTo: React.FC<Props> = ({ variant }) => {
  const [isReadMore, setIsReadMore] = useState(true)
  const [hasOverflow, setHasOverflow] = useState(false)
  const textRef = React.createRef<HTMLDivElement>()

  const { track } = useSegment()

  const { howTo, caution, videos } = variant
  const { description = "", icons = [] } = howTo || {}
  const video = videos?.find((vid) => vid?.videoType === "howto")
  const html = description ?? ""

  const toggleReadMore = () => {
    setIsReadMore(!isReadMore)
    if (isReadMore) {
      track({
        event: "Page Element Clicked",
        properties: {
          module_content: `How to use PDP Read More`,
        },
      })
    }
  }

  useEffect(() => {
    if (textRef?.current) {
      const { offsetHeight } = textRef.current
      if (offsetHeight > scrollThreshold) {
        setHasOverflow(true)
      }
    }
  }, [textRef])

  let boxHeight = "unset"
  let readMore: string | JSX.Element = "Show less"

  if (isReadMore && hasOverflow) {
    boxHeight = "3rem"
    readMore = "Read More"
  }
  return (
    <div>
      {video && (
        <VideoPlayer
          mt={{ base: "-8px", lg: 1 }}
          mb={2}
          description={video.description}
          options={{
            html5: {
              nativeTextTracks: false,
            },
            playsinline: true,
            autoplay: false,
            controls: true,
            responsive: true,
            fluid: true,
            aspectRatio: "1:1",
            sources: video.sources.map(({ uri, contentType }) => ({
              src: uri,
              type: contentType,
            })),
          }}
          videoProps={{
            title: video.name,
            "aria-label": video.name,
          }}
          coverImageProps={{
            src: video.imageUrl,
            title: video.name,
            alt: video.name,
            sizes: "(max-width: 1024px) 100vw, 50vw",
          }}
          videoPosition="PDP how to"
        />
      )}

      <Box
        overflow="hidden"
        position="relative"
        ref={textRef}
        lineHeight={0}
        style={{ maxHeight: boxHeight }}
        onClick={() => {
          if (hasOverflow) toggleReadMore()
        }}
        _before={{ content: "''" }}
      >
        <GenericMarkdown content={html} wrapper="div" forceWrapper />

        {!!icons?.length && (
          <Flex justifyContent="flex-start" mt={3}>
            {icons.map((icon) => {
              if (!icon.label || !mapIcons[icon.label]) return null

              return (
                <Box
                  key={icon.label}
                  pr={1}
                  display="flex"
                  flexDir="column"
                  alignItems="center"
                >
                  <Box padding={-1} border="1px solid black">
                    {mapIcons[icon.label]}
                  </Box>

                  <Text variant="acuminLight" mt={-1}>
                    {icon.value}
                  </Text>
                </Box>
              )
            })}
          </Flex>
        )}

        {!!caution?.label?.length && !!caution?.value?.length && (
          <Flex direction="column" mt={3}>
            <Heading
              as="h2"
              variant="acuminMedium"
              mb={-1}
              fontSize={1}
              lineHeight="24px"
            >
              {caution.label}
            </Heading>
            <GenericMarkdown content={caution.value} />
          </Flex>
        )}
      </Box>
      {hasOverflow && (
        <Text
          data-testid="ReadMore"
          textDecoration="underline"
          variant="acuminRegular"
          mt={1}
          onClick={toggleReadMore}
          sx={{
            ":hover": { cursor: "pointer" },
          }}
          textAlign="left"
        >
          {readMore}
        </Text>
      )}
    </div>
  )
}
